//-------------------------------------------------------------------------------------------------- Imports
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input } from "antd";
import { ArrowUpOutlined } from "@ant-design/icons";
import SubDefaultLayout from "../../components/SubDefaultLayout";
import { useSelector } from "react-redux";
import { getGeneralData } from "../../stores/slices/generalDataSlice";
import { getConfigurableServiceState } from "../../stores/slices/configurableServicesSlice";
import { postMeganChat, streamMeganChat } from "../../methods/Methods";
import type { RequestMeganChat } from "../../types/common";
import { images } from "../../routes/ImageRoutes";

//-------------------------------------------------------------------------------------------------- Types
type ChatMessage = {
    id: string;
    role: "user" | "assistant";
    text: string;
};

//-------------------------------------------------------------------------------------------------- TypingDots
const TypingDots = () => (
    <div style={styles.typingBubble}>
        <span style={styles.typingDot} className="megan-dot megan-dot-1" />
        <span style={styles.typingDot} className="megan-dot megan-dot-2" />
        <span style={styles.typingDot} className="megan-dot megan-dot-3" />
    </div>
);

//-------------------------------------------------------------------------------------------------- View
const MeganChat = () => {
    const generalData = useSelector(getGeneralData);
    const confService = useSelector(getConfigurableServiceState);

    const useStream = useMemo(
        () =>
            confService.dataList.some(
                (s) => s.name === "ACTIVATE_AI_SUPPORT_MEGAN_STREAM" && s.enabled
            ),
        [confService.dataList]
    );

    const [idCliente, setIdCliente] = useState("");
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [inputValue, setInputValue] = useState("");
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isSending, setIsSending] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [showTypingIndicator, setShowTypingIndicator] = useState(false);
    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (generalData.selectedContract !== null) {
            setIdCliente(
                generalData.selectedContract.sucursal + generalData.selectedContract.suscriptor
            );
        }
    }, [generalData.selectedContract]);

    useEffect(() => {
        const timer = setTimeout(() => {
            bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 80);
        return () => clearTimeout(timer);
    }, [messages.length, isSending]);

    const appendMessage = useCallback((role: "user" | "assistant", text: string) => {
        setMessages((prev) => [
            ...prev,
            { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, role, text },
        ]);
    }, []);

    const appendMessageWithTyping = useCallback(async (text: string, tokens?: string[]) => {
        const msgId = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setIsTyping(true);
        setMessages((prev) => [...prev, { id: msgId, role: "assistant", text: "" }]);
        const parts =
            tokens ?? text.split(" ").map((w, i, arr) => (i < arr.length - 1 ? w + " " : w));
        let accumulated = "";
        for (const part of parts) {
            await new Promise<void>((resolve) => setTimeout(resolve, 40));
            accumulated += part;
            const snapshot = accumulated;
            setMessages((prev) =>
                prev.map((m) => (m.id === msgId ? { ...m, text: snapshot } : m))
            );
            bottomRef.current?.scrollIntoView({ behavior: "auto" });
        }
        setIsTyping(false);
    }, []);

    const sendQuestion = useCallback(async () => {
        const question = inputValue.trim();
        if (!question || !idCliente || isSending) return;

        appendMessage("user", question);
        setInputValue("");
        setIsSending(true);
        if (!useStream) setShowTypingIndicator(true);

        const body: RequestMeganChat = {
            message: question,
            conversation_id: conversationId,
            account_number: idCliente,
            call_origin: "WEB",
        };

        try {
            if (useStream) {
                const response = await streamMeganChat(body);
                const events = response?.data?.data ?? [];
                const convInfo = events.find((e) => e.type === "conversation_info");
                if (convInfo?.conversation_id) setConversationId(convInfo.conversation_id);
                const streamTokens = events
                    .filter((e) => e.type === "stream" && typeof e.data === "string")
                    .map((e) => e.data as string);
                const text =
                    streamTokens.join("").trim() ||
                    "No fue posible obtener una respuesta de Megan.";
                setIsSending(false);
                await appendMessageWithTyping(text, streamTokens.length > 0 ? streamTokens : undefined);
            } else {
                const response = await postMeganChat(body);
                const result = response?.data?.data?.[0];
                setConversationId(result?.conversation_id ?? null);
                const finalOutput = result?.execution_log?.find((item) => item.type === "final_output");
                appendMessage(
                    "assistant",
                    finalOutput?.data?.trim() || "No fue posible obtener una respuesta de Megan."
                );
            }
        } catch {
            appendMessage(
                "assistant",
                "No fue posible realizar la consulta, intenta nuevamente en unos momentos."
            );
        } finally {
            setIsSending(false);
            setShowTypingIndicator(false);
        }
    }, [idCliente, isSending, inputValue, conversationId, useStream, appendMessage, appendMessageWithTyping]);

    const canSend = inputValue.trim().length > 0 && !isSending && !isTyping;

    return (
        <SubDefaultLayout title={"Megan"} needReturn={true} navigateTo={"/technical-support"}>
            <style>{`
                @keyframes meganBounce {
                    0%, 100% { transform: translateY(0); opacity: 0.35; }
                    50% { transform: translateY(-5px); opacity: 1; }
                }
                .megan-dot { animation: meganBounce 0.84s infinite; }
                .megan-dot-1 { animation-delay: 0s; }
                .megan-dot-2 { animation-delay: 0.16s; }
                .megan-dot-3 { animation-delay: 0.32s; }
                .megan-input-wrap .ant-input { background: transparent !important; }
                .megan-input-wrap .ant-input::placeholder { color: #b5b5b5; }
            `}</style>

            {/* Marca de agua fija, centrada en el área de contenido */}
            <div style={styles.watermarkOverlay} aria-hidden="true">
                <img src={images.meganSupport} alt="" style={styles.watermarkImage} />
            </div>

            <div style={styles.container}>
                {messages.length === 0 ? (
                    <div style={styles.promptContainer}>
                        <span style={styles.promptText}>¿En qué te puedo ayudar?</span>
                    </div>
                ) : null}

                <div style={styles.messagesContent}>
                    {messages.map((message) => (
                        <div
                            key={message.id}
                            style={{
                                ...styles.bubble,
                                ...(message.role === "assistant" ? styles.assistantBubble : styles.userBubble),
                            }}
                        >
                            <span style={styles.bubbleText}>{message.text}</span>
                        </div>
                    ))}
                    {(isSending || showTypingIndicator) ? <TypingDots /> : null}
                    <div ref={bottomRef} />
                </div>

                <div style={styles.inputBar} className="megan-input-wrap">
                    <Input
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder="Pregúntale a Megan"
                        onPressEnter={sendQuestion}
                        disabled={isSending || isTyping}
                        bordered={false}
                        style={styles.input}
                    />
                    <Button
                        type="primary"
                        shape="circle"
                        icon={<ArrowUpOutlined />}
                        onClick={sendQuestion}
                        disabled={!canSend}
                        size="small"
                        style={{
                            ...styles.sendButton,
                            backgroundColor: canSend ? "#7a1cff" : "#d1d1d1",
                            borderColor: "transparent",
                        }}
                    />
                </div>
            </div>
        </SubDefaultLayout>
    );
};

//-------------------------------------------------------------------------------------------------- Styles
const styles: Record<string, React.CSSProperties> = {
    container: {
        maxWidth: 700,
        margin: "0 auto",
        padding: "0 10px 80px",
    },
    promptContainer: {
        marginTop: 10,
        textAlign: "center",
    },
    promptText: {
        fontFamily: "Montserrat-SemiBold, sans-serif",
        fontSize: 22,
        color: "#9a9a9a",
    },
    watermarkOverlay: {
        position: "fixed",
        top: "calc(50% + 35px)",
        left: "calc(50% + 150px)",
        transform: "translate(-50%, -50%)",
        pointerEvents: "none",
        zIndex: 0,
    },
    watermarkImage: {
        width: "85%",
        opacity: 0.75,
        userSelect: "none",
        filter: "brightness(0) invert(0.85)",
    },
    messagesContent: {
        display: "flex",
        flexDirection: "column",
        width: "92%",
        margin: "0 auto",
        paddingTop: 10,
        paddingBottom: 10,
        position: "relative",
        zIndex: 1,
    },
    bubble: {
        maxWidth: "70%",
        borderRadius: 12,
        padding: "14px 16px",
        marginBottom: 10,
    },
    assistantBubble: {
        alignSelf: "flex-start",
        backgroundColor: "#efe5ff",
    },
    userBubble: {
        alignSelf: "flex-end",
        backgroundColor: "#ebebeb",
    },
    bubbleText: {
        fontFamily: "Montserrat-Regular, sans-serif",
        fontSize: 14,
        color: "#565656",
        lineHeight: "20px",
        whiteSpace: "pre-wrap",
    },
    typingBubble: {
        alignSelf: "flex-start",
        backgroundColor: "#efe5ff",
        borderRadius: 12,
        padding: "14px 16px",
        marginBottom: 10,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
    },
    typingDot: {
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: "#7a1cff",
    },
    inputBar: {
        position: "fixed",
        bottom: 20,
        left: "calc(50% + 150px)",
        transform: "translateX(-50%)",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: "#f3f3f3",
        borderRadius: 24,
        border: "1.5px solid #e0d4f7",
        paddingLeft: 16,
        paddingRight: 8,
        boxShadow: "0 2px 8px rgba(122, 28, 255, 0.10)",
        width: "min(92%, 640px)",
        zIndex: 100,
    },
    input: {
        flex: 1,
        height: 44,
        fontFamily: "Montserrat-Regular, sans-serif",
        fontSize: 14,
        color: "#6b6b6b",
        backgroundColor: "transparent",
    },
    sendButton: {
        width: 32,
        height: 32,
        minWidth: 32,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
};

//-------------------------------------------------------------------------------------------------- Exports
export default MeganChat;
